/**
 * Block-specific arg validation.
 *
 * This module adapts the shared arg validation utilities from args.ts
 * for use with blocks. Key differences from condition arg validation:
 * - Supports "default" values (conditions don't use defaults)
 * - Validates "required + default" contradiction
 * - Supports childArgs with "unique" property
 */
import type {
  ArgSchema,
  ChildArgSchema,
  LayoutEntry,
} from "discourse/blocks/types";
import { getBlockMetadata } from "discourse/lib/blocks/-internals/decorator";
import {
  BlockError,
  raiseBlockError,
} from "discourse/lib/blocks/-internals/error";
import type { BlockClass } from "discourse/lib/blocks/-internals/types";
import {
  VALID_ARG_SCHEMA_PROPERTIES,
  validateArgName,
  validateArgsAgainstSchema,
  validateArgSchemaEntry,
  validateArgValue,
  type ValidateArgValueOptions,
} from "discourse/lib/blocks/-internals/validation/args";

/**
 * Valid properties for childArgs schema definitions.
 * Includes all standard arg properties plus "unique" for sibling uniqueness validation.
 */
export const VALID_CHILD_ARG_SCHEMA_PROPERTIES: readonly string[] =
  Object.freeze([...VALID_ARG_SCHEMA_PROPERTIES, "unique"]);

/**
 * Validates block-specific default value rules:
 * - "required + default" is contradictory (an arg with a default is never missing)
 * - Default value must match the arg's type schema
 *
 * @param argDef - The argument definition.
 * @param argName - The argument name.
 * @param blockName - Block name for error messages.
 * @param argLabel - Label for error messages (e.g., "childArgs arg").
 */
function validateBlockDefaultValue(
  argDef: ArgSchema,
  argName: string,
  blockName: string,
  argLabel = "arg"
): void {
  // Check for required + default contradiction (value-based, not presence-based)
  // An arg with required: false + default is valid, so we check required === true
  if (argDef.required === true && argDef.default !== undefined) {
    raiseBlockError(
      `Block "${blockName}": ${argLabel} "${argName}" has both "required: true" and "default". ` +
        `These options are contradictory - an arg with a default value is never missing.`
    );
  }

  if (argDef.default !== undefined) {
    const defaultError = validateArgValue(argDef.default, argDef, argName, {
      contextName: blockName,
      contextType: "Block",
    });
    if (defaultError) {
      raiseBlockError(
        `Block "${blockName}": ${argLabel} "${argName}" has invalid default value. ${defaultError.message}`
      );
    }
  }
}

/**
 * Validates the arg schema definition passed to the `@block` decorator.
 * Enforces strict schema format - unknown properties are not allowed.
 *
 * @param argsSchema - The args schema object from decorator options.
 * @param blockName - Block name for error messages.
 * @throws Error if schema is invalid.
 */
export function validateArgsSchema(
  argsSchema: Record<string, ArgSchema> | null | undefined,
  blockName: string
): void {
  if (!argsSchema || typeof argsSchema !== "object") {
    return;
  }

  for (const [argName, argDef] of Object.entries(argsSchema)) {
    if (
      !validateArgName(argName, { entityName: blockName, entityType: "Block" })
    ) {
      continue;
    }

    const shouldContinue = validateArgSchemaEntry(argDef, argName, {
      entityName: blockName,
      entityType: "Block",
      validProperties: VALID_ARG_SCHEMA_PROPERTIES,
    });

    if (!shouldContinue) {
      continue;
    }

    validateBlockDefaultValue(argDef, argName, blockName);
  }
}

/**
 * Validates block arguments against the block's metadata arg schema.
 * Checks for required args and validates types.
 *
 * @param entry - The block entry.
 * @param blockClass - The resolved block class (must be a class, not a string reference).
 * @param options - Optional configuration.
 * @throws BlockError if args are invalid.
 */
export function validateBlockArgs(
  entry: Pick<LayoutEntry, "args">,
  blockClass: BlockClass,
  options: ValidateArgValueOptions = {}
): void {
  const metadata = getBlockMetadata(blockClass);
  const providedArgs = entry.args || {};
  const hasProvidedArgs = Object.keys(providedArgs).length > 0;
  const argsSchema = metadata?.args;

  // If args are provided but no schema exists, reject them
  if (hasProvidedArgs && !argsSchema) {
    const argNames = Object.keys(providedArgs).join(", ");
    throw new BlockError(
      `args were provided (${argNames}) but this block does not declare an args schema. ` +
        `Add an args schema to the @block decorator or remove the args.`,
      { path: "args" }
    );
  }

  // No schema and no args - nothing to validate
  if (!argsSchema) {
    return;
  }

  validateArgsAgainstSchema(providedArgs, argsSchema, "args", options);
}

/**
 * Validates the childArgs schema definition passed to the `@block` decorator.
 * Similar to validateArgsSchema but supports the additional "unique" property
 * for enforcing uniqueness across sibling children.
 *
 * @param childArgsSchema - The childArgs schema object from decorator options.
 * @param blockName - Block name for error messages.
 * @throws Error if schema is invalid.
 */
export function validateChildArgsSchema(
  childArgsSchema: Record<string, ChildArgSchema> | null | undefined,
  blockName: string
): void {
  if (!childArgsSchema || typeof childArgsSchema !== "object") {
    return;
  }

  for (const [argName, argDef] of Object.entries(childArgsSchema)) {
    if (
      !validateArgName(argName, {
        entityName: blockName,
        entityType: "Block",
        argLabel: "childArgs arg",
      })
    ) {
      continue;
    }

    const shouldContinue = validateArgSchemaEntry(argDef, argName, {
      entityName: blockName,
      entityType: "Block",
      validProperties: VALID_CHILD_ARG_SCHEMA_PROPERTIES,
      argLabel: "childArgs arg",
    });

    if (!shouldContinue) {
      continue;
    }

    // childArgs-specific: validate "unique" is a boolean if provided
    if (argDef.unique !== undefined && typeof argDef.unique !== "boolean") {
      raiseBlockError(
        `Block "${blockName}": childArgs arg "${argName}" has invalid "unique" value. Must be a boolean.`
      );
    }

    // childArgs-specific: validate "unique" is only used with primitive types
    if (argDef.unique === true && argDef.type === "array") {
      raiseBlockError(
        `Block "${blockName}": childArgs arg "${argName}" has "unique: true" but type is "array". ` +
          `Uniqueness validation is only supported for primitive types (string, number, boolean).`
      );
    }

    validateBlockDefaultValue(argDef, argName, blockName, "childArgs arg");
  }
}
